// English is the default locale. Every other dictionary is typed against this
// one, so a missing or stale key is a compile error rather than a blank label
// that only shows up when somebody switches language.
export const en = {
  // -- shell --
  "app.tagline": "Turn an image into a Cricut-ready SVG",
  "app.account": "Your account",
  "app.back": "Back",
  "app.signOut": "Sign out",
  "update.available": "A new version of crikea is ready.",
  "update.reload": "Reload",

  // -- source image --
  "source.drop": "Drop an image here, or",
  "source.browse": "browse",
  "source.hint": "PNG or JPG. Line art works best.",
  "source.replace": "Replace image",
  "source.clean": "Clean up with AI",
  "source.cleaning": "Cleaning…",
  "source.cleanHint": "Removes the background and flattens it to black on white.",
  "source.erase": "Erase areas",
  "source.eraseDone": "Done erasing",
  "source.eraseHint": "Drag over anything that should not be cut.",
  "erase.aria": "Drag to erase parts of the design",
  "source.compare": "Compare with the original",

  // -- parameters --
  "params.mode": "Mode",
  "params.mode.cut": "Cut",
  "params.mode.draw": "Draw",
  "params.side": "Fix the",
  "params.side.width": "width",
  "params.side.height": "height",
  "params.size": "Size",
  "params.rotation": "Rotation",
  "params.thicken": "Thickening",
  "params.thickenAuto": "Automatic",
  "params.thickenHint": "Thin strokes do not survive the blade. The default follows the material.",

  // -- audit --
  "audit.title": "Check",
  "audit.thinnest": "Thinnest stroke",
  "audit.nodes": "Nodes",
  "audit.size": "Final size",
  "audit.ok": "Ready to cut",
  "audit.warn": "Cuttable, with risk",
  "audit.fail": "It will not cut well",
  "audit.busy": "Measuring…",

  // -- actions --
  "action.download": "Download SVG",
  "action.save": "Save to history",
  "action.saving": "Saving…",
  "action.saved": "Saved to your history.",

  // -- history --
  "history.title": "History",
  "history.empty": "Nothing saved yet.",
  "history.yours": "Your designs",
  "history.emptyLong": "Nothing saved yet. Convert an image and save it to find it here.",
  "history.loadingYours": "Loading your history…",
  "history.deleteLabel": "Delete {name}",
  "history.open": "Open",
  "history.delete": "Delete",
  "history.confirmDelete": "Delete this design?",
  "history.loading": "Loading…",

  // -- auth --
  "auth.signIn": "Sign in",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.signingIn": "Signing in…",
  "auth.forgot": "Forgot your password?",
  "auth.passkey": "Sign in with your fingerprint",
  "auth.passkeyHint": "No email, no password.",
  "auth.backToSignIn": "Back to sign in",

  "reset.title": "Choose a new password",
  "reset.new": "New password",
  "reset.confirm": "Repeat it",
  "reset.submit": "Save the password",
  "reset.saving": "Saving…",
  "reset.mismatch": "The two passwords do not match.",
  "reset.sendLink": "Send me the link",
  "reset.sending": "Sending…",
  "reset.linkSent": "If that account exists, we have emailed you the link.",
  "reset.done": "Done. You can sign in with your new password now.",
  "reset.minHint": "At least {min} characters. Long beats complicated.",
  "reset.readyTitle": "Done",

  // -- settings --
  "settings.title": "Your account",
  "settings.language": "Language",
  "settings.language.hint": "Applies to this device.",
  "settings.password": "Password",
  "settings.current": "Current password",
  "settings.new": "New password",
  "settings.change": "Change it",
  "settings.changing": "Changing…",
  "settings.changed": "Done, your password has been changed.",

  "passkey.title": "Sign in with your fingerprint",
  "passkey.blurb":
    "Faster than a password, and it cannot be phished: the key never leaves this device.",
  "passkey.enable": "Turn on",
  "passkey.enabling": "Waiting for the device…",
  "passkey.unsupported": "This device does not support it.",
  "passkey.devices": "Your devices",
  "passkey.added": "Added",
  "passkey.lastUsed": "Last used",
  "passkey.never": "never",
  "passkey.backedUp": "Backed up",
  "passkey.thisDevice": "This device only",
  "passkey.rename": "Rename",
  "passkey.remove": "Remove",
  "passkey.confirmRemove": "Remove this device?",
  "passkey.blurbLong":
    "Register this device and sign in with your fingerprint or face, without typing anything. The key stays on the device: not even we have it.",
  "passkey.unsupportedLong":
    "This device cannot use fingerprint or face. You can still sign in with your password.",
  "passkey.registerAnother": "Register this device",
  "passkey.registering": "Registering…",
  "passkey.usedOn": "used on {date}",
  "passkey.unused": "not used yet",
  "passkey.backedUpShort": "backed up",
  "passkey.thisDeviceOnly": "on this device only",
  "passkey.removeLabel": "Remove {label}",
  "device.iphone": "My iPhone",
  "device.ipad": "My iPad",
  "device.android": "My phone",
  "device.mac": "My Mac",
  "device.windows": "My PC",
  "device.generic": "My device",
  "passkey.registered": "Done. You can now sign in with your fingerprint.",

  "mode.hint.cut": "Thickens the stroke so it survives weeding in vinyl.",
  "mode.hint.draw": "Leaves the stroke thin: the pen tip merges the double contour.",
  "audit.summary.draw": "Draw mode: a thin stroke is what you want, so nothing is thickened.",
  "audit.summary.fail": "{pct} of the stroke is under {mm} mm -- it will tear while weeding.",
  "audit.summary.warn": "{pct} is under the {mm} mm floor -- cuttable with a fresh blade and patience.",
  "audit.summary.ok": "Nothing below {mm} mm. Ready to cut.",
  "fit.tooWide":
    "It does not fit: the short side is {short} mm and the machine cuts up to {max} mm wide.",
  "fit.tooLong": "That is {long} mm long -- it fits the machine, but it is a lot for a shirt.",
  "audit.threshold": "Threshold",
  "audit.thinnestInk": "Thinnest ink",
  "audit.nodesLabel": "nodes",
  "trace.working": "Tracing…",
  "app.loading": "Loading…",
  "source.dropOrTap": "Drop an image or tap to choose one",
  "source.cleaned": "Image cleaned. Check the result before saving.",
  "source.clearErased": "Clear {count} erased",
  "source.eraseActiveHint":
    "Drag over the drawing to cover what you do not want. Tap a rectangle to remove it.",
  "source.cleanIdleHint": "Removes the background and leaves the design pure black on white.",
  "compare.original": "original",
  "compare.thickened": "thickened",
  "save.savedNoImage": "Saved, but we could not archive the original image.",
  "history.openedNoImage":
    "You opened \"{name}\", but it has no stored image. Drop it again to re-export it.",
  "history.opened": "You opened \"{name}\". Change its size or mode and download it again.",
  "history.openedRetry": "You opened \"{name}\". Drop the image again to re-export it.",
  "params.thickenLegend": "Thickening",
  "params.floorHint": "Recommended floor for letters and logos: {mm} mm.",
  "params.compareStroke": "Compare with the original stroke",
  "params.outline": "Outline only",
  "params.outline.hint": "Fills in eyes and inner details: only the outer edge gets cut or drawn.",
  "params.closeGaps": "Close gaps",
  "params.closeGaps.hint": "If inner details still show up, raise this: the outline has a gap somewhere.",
  "params.largestOnly": "Only the largest piece",
  "params.outlineMargin": "Margin",
  "params.outlineMargin.edge": "edge to edge",
  "params.outlineMargin.hint": "Grows the piece around the drawing, with round corners -- a backing layer, a topper's base. The size above stays the drawing's, so both pieces line up.",
  "history.outlineTag": "Outline",
  "params.widthOption": "Width",
  "params.heightOption": "Height",

  // -- errors --
  // Keys mirror the API's stable error codes, so httpx.Error.Code maps straight
  // into this table and the wording can change on either side independently.
  "error.bad_request": "We could not understand that request.",
  "error.invalid_credentials": "Wrong email or password.",
  "error.session_failed": "We could not start your session.",
  "error.internal": "Something went wrong on our side.",
  "error.login_required": "Sign in to continue.",
  "error.session_invalid": "Your session is not valid. Sign in again.",
  "error.session_expired": "Your session expired. Sign in again.",
  "error.file_too_large": "That file is too large.",
  "error.password_change_unavailable": "Changing your password is unavailable right now.",
  "error.reset_rate_limited":
    "You have asked for several links already. Wait a moment before trying again.",
  "error.reset_link_failed": "We could not prepare the link.",
  "error.reset_email_failed": "We could not send the email. Try again.",
  "error.reset_link_invalid":
    "That link has expired or was already used. Ask for a new one from the sign-in screen.",
  "error.password_change_failed": "We could not change your password.",
  "error.current_password_wrong": "Your current password is not correct.",
  "error.password_required": "Enter a password.",
  "error.password_too_short": "That password is too short.",
  "error.password_too_long": "That password is too long.",
  "error.passkeys_unavailable": "Passkey sign-in is unavailable right now.",
  "error.passkey_register_prepare_failed": "We could not prepare the registration.",
  "error.passkey_register_expired": "The registration expired. Try again.",
  "error.passkey_register_failed": "We could not register this device.",
  "error.passkey_login_prepare_failed": "We could not prepare sign-in.",
  "error.passkey_login_expired": "Sign-in expired. Try again.",
  "error.passkey_login_failed": "We could not sign you in with that device.",
  "error.passkey_register_cancelled": "Registration was cancelled or timed out.",
  "error.passkey_login_cancelled": "Sign-in was cancelled or timed out.",
  "error.passkey_already_registered": "This device is already registered on your account.",
  "error.passkey_unsupported": "This device cannot use passkeys.",
  "error.passkey_insecure": "This site is not secure enough for passkeys.",
  "error.passkey_list_failed": "We could not load your devices.",
  "error.passkey_not_found": "That device is not on your list.",
  "error.passkey_delete_failed": "We could not remove the device.",
  "error.clean_rate_limited":
    "You are cleaning a lot of images in a row. Try again in a moment.",
  "error.image_required": "Send an image.",
  "error.image_empty": "The image is empty.",
  "error.clean_failed":
    "We could not clean this image. Try one where the design shows up more clearly.",
  "error.clean_unavailable": "The cleaning service did not respond.",
  "error.design_save_failed": "We could not save the design.",
  "error.design_not_found": "That design does not exist.",
  "error.history_load_failed": "We could not load your history.",
  "error.design_open_failed": "We could not open the design.",
  "error.design_delete_failed": "We could not delete the design.",
  "error.blobs_unavailable": "Image storage is unavailable right now.",
  "error.image_too_large": "The image is too large.",
  "error.image_save_failed": "We could not save the image.",
  "error.image_open_failed": "We could not open the image.",
  "error.image_not_found": "This design has no stored image.",
  "error.name_required": "Give the design a name.",
  "error.name_too_long": "That name is too long.",
  "error.mode_invalid": "Mode has to be cut or draw.",
  "error.size_invalid": "The measurements have to be greater than zero.",
  "error.size_too_large": "The measurements exceed what the machine can cut.",
  "error.rotation_invalid": "Rotation has to be 0, 90, 180 or 270 degrees.",
  "error.svg_required": "The SVG is missing.",
  "error.thicken_invalid": "Thickening cannot be negative.",
  "error.close_gaps_invalid": "Closing gaps has to be between 0 and 20 mm.",
  "error.outline_margin_invalid": "The outline margin has to be between 0 and 50 mm.",

  // -- client-side failures, which never reach the API --
  "error.readImage": "I could not read the image: {detail}",
  "error.network": "No connection. Check your network and try again.",
  "error.unknown": "Something went wrong.",
} as const;

export type Key = keyof typeof en;
